import React, { useState, useEffect } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import api from '../services/api';
import { isValidEmail } from '../utils/Utils';
import { presignAndUploadFile } from '../services/storageService';
import TextInput from './ui/TextInput.jsx';
import RichTextInput from './ui/RichTextInput.jsx';
import GenericSelect from './ui/GenericSelect';
import CheckboxInput from './ui/CheckboxInput';
import PhoneNumberInput from './ui/PhoneNumberInput';
import { toE164 } from '../utils/phone';
import DateTimePicker from './ui/DateTimePicker';
import TimezoneSelector from './ui/TimezoneSelector';
import PictureUpload from './ui/PictureUpload';
import AsyncGenericSelect from './ui/AsyncGenericSelect';
import LanguageSelect from './ui/LanguageSelect';
import EntitlementRulesEditor from './ui/EntitlementRulesEditor';
import GroupCriteriaBuilder from './ui/GroupCriteriaBuilder';
import SortableList from './ui/SortableList';
import TaskTemplateItemsEditor from './ui/TaskTemplateItemsEditor';
import LeavePolicyTemplateVariationsEditor from './ui/LeavePolicyTemplateVariationsEditor';
import ColorPicker from './ui/ColorPicker';
import AssignmentTargetPicker from './ui/AssignmentTargetPicker';
import Button from './ui/Button';
import useLayoutBucket from '../hooks/useLayoutBucket';
import { drawerGeometry } from './layout/shellMetrics';


const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
  flex-shrink: 0;

  @media (max-width: 600px) {
    padding: 14px 16px;
  }
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

const Body = styled.div`
  flex: 1;
  /* Çekmecenin TEK kaydırma kabı burasıdır: Header ve Footer "flex-shrink: 0"
     taşıdığından çekmecenin kendisi hiç kaymaz, yalnız form gövdesi kayar. */
  min-height: 0;
  overflow-y: auto;
  padding: 24px;

  /* FE-08 — telefonda 24px × 2 = 48px, 390px'lik bir çekmecenin %12'si. */
  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 18px;

  @media (max-width: 600px) {
    & > * { grid-column: span 12 !important; }
  }
`;

const GridItem = styled.div`
  grid-column: span ${({ $span }) => $span || 12};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #E8ECF0;
  flex-shrink: 0;

  /* FE-08 — telefonda "İptal" ve "Kaydet" tam genişliğe yayılır: 44px'lik
     dokunma hedefi, sağ kenara sıkışmış iki küçük düğme yerine. */
  @media (max-width: 600px) {
    padding: 12px 16px;
    gap: 8px;

    & > * {
      flex: 1;
    }
  }
`;

const Description = styled.p`
  font-size: 11px;
  color: #A0A8B4;
  margin-top: 4px;
`;

// `notice` alan tipinin kutusu. Girdi DEĞİL: kullanıcıya işlemin ne yapacağını
// anlatan durağan bir blok. Alanın `description`'ı (11px, soluk) bunun yerini
// tutmuyordu — bir aksiyonun sonuçlarını sayan metin, bir alanın ipucundan daha
// görünür olmalı ve hiçbir alana bağlı olmamalı.
const Notice = styled.div`
  background: ${({ $tone }) => ($tone === 'warning' ? '#FFF8E6' : '#EEF6FD')};
  border-left: 3px solid ${({ $tone }) => ($tone === 'warning' ? '#FFC107' : '#0291EF')};
  border-radius: 4px;
  padding: 10px 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;

  ul {
    margin: 6px 0 0;
    padding-left: 18px;
  }

  li + li {
    margin-top: 2px;
  }
`;

// Pure — no component state, so behaviour can be pinned without rendering the
// drawer (see GenericFormDrawer.purpose.test.js). Uploads every `pictureUpload`
// field whose value is a File: presign → PUT to R2 → replace with file_url.
// A field's `purpose` (if declared) is forwarded to the presign call; the
// server maps purpose → visibility bucket (see backend StoragePurposes) and
// treats a missing purpose as Private — so fields with no `purpose` simply
// omit the query param, which is deliberate, not an oversight.
export const uploadSchemaFiles = async (schema, data, initialValues) => {
  const result = { ...data };
  for (const field of schema) {
    if (field.type !== 'pictureUpload') continue;
    const newValue = result[field.name];
    const oldValue = initialValues?.[field.name];

    // New file selected
    if (newValue instanceof File) {
      // Get presigned URL
      try {
        result[field.name] = await presignAndUploadFile(newValue, field.purpose);
      } catch (error) {
        console.log(error)
      }

    }

    // File removed — clear the reference; the orphaned object is reclaimed
    // by the storage GC sweep (no client-side delete — storage security fix)
    if (newValue === null && oldValue && typeof oldValue === 'string') {
      result[field.name] = '';
    }
  }
  return result;
};

export default function GenericFormDrawer({
  open,
  onClose,
  title,
  schema = [],
  initialValues,
  onSave,
  children,
  // Kaydetmeyen eylemler (ör. toplu işi başlatma) için düğme metni ve kilidi —
  // verilmezse davranış AYNEN eski: "Kaydet", yalnız kayıt sürerken kilitli.
  submitLabel,
  submitDisabled = false,
  // `children` şema alanlarına göre nerede çizilir: 'bottom' (varsayılan, eski davranış —
  // alanlardan SONRA) ya da 'top' (alanlardan ÖNCE; ör. KVKK İK incelemesinde talebin
  // açıklaması/damgaları karar alanlarından önce okunur).
  childrenPosition = 'bottom',
}) {
  const { t } = useTranslation();
  // FE-08 — çekmecenin genişliği `react-modern-drawer`'a prop olarak gider,
  // CSS sınıfı olarak değil; bir `@media` bloğu ona ulaşamaz. Bu yüzden karar
  // kırılım kovasından okunuyor.
  const bucket = useLayoutBucket();
  const geometry = drawerGeometry(bucket);
  const [formData, setFormData] = useState({});
  const [errorFields, setErrorFields] = useState([]);
  // Per-field custom error messages (e.g. date out-of-range), keyed by field name.
  const [fieldErrors, setFieldErrors] = useState({});
  // Field names currently being populated by an in-flight deriveFields() — drives
  // the loading spinner on the dependent select(s).
  const [derivingFields, setDerivingFields] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // Must initialize synchronously — any delay races against child selectors
      // (e.g. TimezoneSelector) that auto-fill a default via onChange on mount.
      // A later setFormData would wipe those writes, producing empty required fields.
      if (initialValues) {
        // Edit mode: use the row as-is (unchanged behavior).
        setFormData(initialValues);
      } else {
        // Create mode: seed the form with each field's schema `defaultValue`.
        const withDefaults = {};
        schema.forEach((f) => {
          if (f.defaultValue !== undefined) withDefaults[f.name] = f.defaultValue;
        });
        setFormData(withDefaults);
      }
      setErrorFields([]);
      setFieldErrors({});
    } else {
      setFormData({});
      setErrorFields([]);
      setFieldErrors({});
    }
  }, [open, initialValues]);

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorFields((prev) => prev.filter((f) => f !== name));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });

    // Cross-field auto-fill: a field may declare deriveFields(value, formData) → patch
    // object that is merged into the form (e.g. picking an employee fills the
    // manager from the org chart). Async; failures leave the form untouched.
    // İkinci argüman bu değişiklik uygulanmış GÜNCEL form değerleridir (ör. "yalnız boşsa doldur"
    // kuralı); tek argüman bekleyen mevcut türetmeler onu yok sayar.
    const field = schema.find((f) => f.name === name);
    if (field?.deriveFields) {
      // Fields this change will auto-populate — show a spinner on them while the
      // (possibly slow) lookup is in flight.
      const derived = Array.isArray(field.derives) ? field.derives : [];
      if (derived.length) setDerivingFields((prev) => new Set([...prev, ...derived]));
      Promise.resolve(field.deriveFields(value, { ...formData, [name]: value }))
        .then((patch) => {
          if (patch && typeof patch === 'object') {
            setFormData((prev) => ({ ...prev, ...patch }));
            setErrorFields((prev) => prev.filter((f) => !(f in patch)));
          }
        })
        .catch(() => {})
        .finally(() => {
          if (derived.length) {
            setDerivingFields((prev) => {
              const next = new Set(prev);
              derived.forEach((f) => next.delete(f));
              return next;
            });
          }
        });
    }
  };

  // `action` alan tipi: formu dolduran bir düğme (ör. uyum profilinde "657 Varsayılanlarını Yükle").
  // deriveFields ile AYNI sözleşme — `onClick(formData)` bir yama nesnesi (ya da Promise'i) döner ve
  // forma birleştirilir; undefined/hata formu değiştirmez. Düğme iş sürerken kilitlidir.
  const runFieldAction = (field) => {
    if (typeof field.onClick !== 'function') return;
    setDerivingFields((prev) => new Set([...prev, field.name]));
    Promise.resolve()
      .then(() => field.onClick(formData))
      .then((patch) => {
        if (patch && typeof patch === 'object') {
          setFormData((prev) => ({ ...prev, ...patch }));
          setErrorFields((prev) => prev.filter((f) => !(f in patch)));
        }
      })
      .catch(() => {})
      .finally(() => {
        setDerivingFields((prev) => {
          const next = new Set(prev);
          next.delete(field.name);
          return next;
        });
      });
  };

  const uploadFiles = (data) => uploadSchemaFiles(schema, data, initialValues);

  const isFieldHidden = (field) =>
    typeof field.hidden === 'function' ? field.hidden(formData) : !!field.hidden;

  // `required` düz boolean ya da `(formData) => boolean` — `hidden`/`label`/`description`
  // ile AYNI sözleşme (ör. KVKK İK notu yalnız "Reddedildi" seçilince zorunlu). Fonksiyon
  // değer olarak truthy'dir; çözülmeden okunursa alan HER ZAMAN zorunlu sayılırdı.
  const isFieldRequired = (field) =>
    typeof field.required === 'function' ? !!field.required(formData) : !!field.required;

  const handleSubmit = async () => {
    // Validate required fields — but skip those currently hidden by their
    // schema condition (e.g. license fields hidden when tenantType=Subsidiary).
    const missing = schema
      .filter((f) => isFieldRequired(f) && !isFieldHidden(f))
      .filter((f) => {
        const val = formData[f.name];
        return val === undefined || val === null || val === '';
      });

    if (missing.length > 0) {
      setErrorFields(missing.map((f) => f.name));
      toast.error(t('common.fillRequired', { defaultValue: 'Zorunlu alanları doldurun.' }));
      return;
    }

    // Validate date fields against their optional minDate/maxDate bounds. This is
    // a SUBMIT-time check on purpose: doing it per-keystroke would reject the
    // out-of-range intermediate years (19, 199) the user types on the way to a
    // valid year and wipe the field. Bounds and values are "yyyy-MM-dd" strings
    // (or Date for bounds); setFullYear avoids the JS 0-99 → 1900s year mapping.
    const toDate = (v) => {
      if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
      if (typeof v !== 'string' || !v) return null;
      const parts = v.split('T')[0].split('-');
      if (parts.length < 3) return null;
      const [y, mo, da] = parts.map(Number);
      const d = new Date(0);
      d.setFullYear(y, mo - 1, da);
      d.setHours(0, 0, 0, 0);
      return isNaN(d.getTime()) ? null : d;
    };
    const rangeErrors = {};
    schema
      .filter((f) => (f.type === 'date' || f.type === 'datetime') && (f.minDate || f.maxDate) && !isFieldHidden(f))
      .forEach((f) => {
        const v = toDate(formData[f.name]);
        if (!v) return; // empty is handled by the required check above
        const min = toDate(f.minDate);
        const max = toDate(f.maxDate);
        if ((min && v < min) || (max && v > max)) {
          rangeErrors[f.name] = t('common.dateOutOfRange', { defaultValue: 'Tarih geçerli aralıkta değil.' });
        }
      });
    if (Object.keys(rangeErrors).length > 0) {
      setFieldErrors(rangeErrors);
      toast.error(t('common.dateOutOfRange', { defaultValue: 'Tarih geçerli aralıkta değil.' }));
      return;
    }

    // Validate e-mail fields (ASCII-only). Empty is allowed; required check above
    // covers mandatory ones.
    const emailErrors = {};
    schema
      .filter((f) => f.type === 'email' && !isFieldHidden(f))
      .forEach((f) => {
        const v = formData[f.name];
        if (v && !isValidEmail(v)) {
          emailErrors[f.name] = t('common.invalidEmail', { defaultValue: 'Geçerli bir e-posta adresi girin (yalnızca İngilizce karakter).' });
        }
      });
    if (Object.keys(emailErrors).length > 0) {
      setFieldErrors(emailErrors);
      toast.error(t('common.invalidEmail', { defaultValue: 'Geçerli bir e-posta adresi girin (yalnızca İngilizce karakter).' }));
      return;
    }

    // `minLength` (text/textarea): baştaki/sondaki boşluk sayılmaz (backend Trim sonrası
    // sayar). Boş değer burada denetlenmez — zorunluluk yukarıdaki required denetiminin işi;
    // yalnız boşluktan oluşan değer ise "kısa" sayılır. Alan vermeyen şemada davranış AYNEN eski.
    const lengthErrors = {};
    schema
      .filter((f) => f.minLength && (f.type === 'text' || f.type === 'textarea') && !isFieldHidden(f))
      .forEach((f) => {
        const v = formData[f.name];
        if (typeof v === 'string' && v !== '' && v.trim().length < f.minLength) {
          lengthErrors[f.name] = t('common.minLength', { min: f.minLength, defaultValue: 'En az {{min}} karakter girin.' });
        }
      });
    if (Object.keys(lengthErrors).length > 0) {
      setFieldErrors(lengthErrors);
      toast.error(Object.values(lengthErrors)[0]);
      return;
    }

    // İnceleme turu (Task 10 düzeltmesi) — Minor bulgu: `leavePolicyTemplateVariations`
    // (LeavePolicyTemplateVariationsEditor) her kartta name/balanceScope/category için
    // GenericSelect/TextInput `required` prop'u taşır, ama bu iki bileşende `required`
    // yalnız kırmızı yıldız ekler — GÖNDERİMİ DURDURMAZ (bkz. GenericSelect.js,
    // TextInput.jsx: `required` hiçbir zaman native input'a veya bu formun üstteki
    // `missing` denetimine ulaşmaz, çünkü o denetim yalnız ÜST SEVİYE alanlara bakar,
    // bu alanların hepsi bir DİZİNİN İÇİNDE). leave-types sayfasındaki tek-varyasyon
    // formu aynı alanları GenericFormDrawer'ın üst seviye required denetiminden
    // geçirir (bloklar); iki form "alan alan aynı" olduğu hâlde davranışça farklıydı.
    // Düzeltilmezse eksik bir tür sessizce kaydedilir ve 400 yalnız şablon gerçekten
    // KULLANILDIĞINDA (LeaveAxisValidator) yüzeye çıkar.
    const variationErrors = {};
    schema
      .filter((f) => f.type === 'leavePolicyTemplateVariations' && !isFieldHidden(f))
      .forEach((f) => {
        const items = Array.isArray(formData[f.name]) ? formData[f.name] : [];
        const incomplete = items.some((it) => !it?.name || !it?.balanceScope || !it?.category);
        if (incomplete) {
          variationErrors[f.name] = t('leavePolicyTemplate.variationsIncomplete', {
            defaultValue: 'Tür listesindeki zorunlu alanları (ad, bakiye kapsamı, kategori) doldurun.',
          });
        }
      });
    if (Object.keys(variationErrors).length > 0) {
      setFieldErrors(variationErrors);
      toast.error(Object.values(variationErrors)[0]);
      return;
    }

    setSaving(true);
    try {
      const processedData = await uploadFiles(formData);
      const success = await onSave?.(processedData);
      if (success !== false) onClose?.();
    } catch {
      // handled by interceptor
    } finally {
      setSaving(false);
    }
  };

  const renderField = (rawField) => {
    // `label` düz metin ya da `(formData) => metin` olabilir — `hidden`,
    // `disabled`, `options` ve `description` ile AYNI sözleşme. Gerekçesi
    // (düzeltme turu 4, Bulgu 1): toplu izin çekmecesinde bitiş tarihi alanının
    // ANLAMI forma girilen `isHourly` değerine göre değişiyor (günlük izinde
    // İŞE BAŞLAMA tarihi, saatlik izinde gerçek bitiş) ve tekil izin formu
    // (MyLeavesPage) bu ayrımı zaten yapıyor. Etiket sabit kaldığı sürece iki
    // form aynı alana FARKLI anlam yüklüyordu.
    const labelled = typeof rawField.label === 'function'
      ? { ...rawField, label: rawField.label(formData) }
      : rawField;
    const field = typeof labelled.required === 'function'
      ? { ...labelled, required: isFieldRequired(labelled) }
      : labelled;

    const hasError = errorFields.includes(field.name);
    const fieldValue = field.value ? field.value(formData) : formData[field.name];

    const commonProps = {
      error: fieldErrors[field.name]
        || (hasError ? t('common.requiredField', { defaultValue: 'Bu alan zorunludur.' }) : undefined),
    };

    switch (field.type) {
      // Girdi değil, açıklama. `content` düz metin ya da JSX olabilir (madde
      // listesi gibi). Değer taşımadığı için formData'ya hiç girmez ve zorunlu
      // alan denetimine takılmaz (required verilmez).
      case 'notice':
        return <Notice $tone={field.tone}>{field.content}</Notice>;

      // Girdi değil, formu dolduran düğme (bkz. runFieldAction). Değer taşımaz.
      case 'action':
        return (
          <Button
            type="button"
            variant={field.variant || 'outline'}
            loading={derivingFields.has(field.name)}
            onClick={() => runFieldAction(field)}
            data-field-action={field.name}
          >
            {field.label}
          </Button>
        );

      case 'text':
      case 'email':
      case 'number':
      case 'password':
        return (
          <TextInput
            label={field.label}
            type={field.type}
            value={fieldValue ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            min={field.min}
            max={typeof field.max === 'function' ? field.max(formData) : field.max}
            step={field.step}
            {...(field.maxLength ? { maxLength: field.maxLength } : {})}
            {...commonProps}
          />
        );

      case 'textarea':
        return (
          <TextInput
            label={field.label}
            type="textarea"
            value={fieldValue ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            rows={field.rows || 4}
            {...(field.maxLength ? { maxLength: field.maxLength } : {})}
            {...commonProps}
          />
        );

      case 'richText':
        return (
          <RichTextInput
            label={field.label}
            value={fieldValue ?? ''}
            onChange={(html) => handleFieldChange(field.name, html)}
            required={field.required}
            rows={field.rows || 6}
            placeholder={field.placeholder}
            {...commonProps}
          />
        );

      case 'date':
        return (
          <DateTimePicker
            label={field.label}
            mode="date"
            value={fieldValue ?? ''}
            onChange={(val) => handleFieldChange(field.name, val || '')}
            required={field.required}
            minDate={field.minDate}
            maxDate={field.maxDate}
            error={commonProps.error}
          />
        );

      case 'datetime':
        return (
          <DateTimePicker
            label={field.label}
            mode="datetime"
            value={fieldValue ?? ''}
            onChange={(val) => handleFieldChange(field.name, val || '')}
            required={field.required}
            minDate={field.minDate}
            maxDate={field.maxDate}
            error={commonProps.error}
          />
        );

      case 'time':
        return (
          <DateTimePicker
            label={field.label}
            mode="time"
            value={fieldValue ?? ''}
            onChange={(val) => handleFieldChange(field.name, val || '')}
            required={field.required}
            error={commonProps.error}
          />
        );

      case 'asyncSelect':
        return (
          <AsyncGenericSelect
            label={field.label}
            value={fieldValue ?? null}
            onChange={(val) => handleFieldChange(field.name, val)}
            onSearch={field.onSearch}
            onGetById={field.onGetById}
            renderOption={field.renderOption}
            searchPlaceholder={field.searchPlaceholder}
            isMulti={field.isMulti}
            required={field.required}
            error={commonProps.error}
            direction={field.direction}
            minChars={field.minChars}
            disabled={typeof field.disabled === 'function' ? field.disabled(formData) : !!field.disabled}
            busy={derivingFields.has(field.name)}
          />
        );

      case 'timezone':
        return (
          <TimezoneSelector
            label={field.label}
            value={fieldValue ?? null}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            error={commonProps.error}
            direction={field.direction}
          />
        );

      case 'select':
        return (
          <GenericSelect
            label={field.label}
            options={typeof field.options === 'function' ? (field.options(formData) || []) : (field.options || [])}
            value={fieldValue ?? null}
            onChange={(val) => handleFieldChange(field.name, val)}
            idKey={field.idKey || 'id'}
            labelKey={field.labelKey || 'name'}
            renderOption={field.renderOption}
            required={field.required}
            error={commonProps.error}
            direction={field.direction}
          />
        );

      case 'languageSelect':
        return (
          <LanguageSelect
            label={field.label}
            value={fieldValue ?? null}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            error={commonProps.error}
          />
        );

      case 'colorPicker':
        return (
          <ColorPicker
            label={field.label}
            value={fieldValue ?? ''}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            error={commonProps.error}
          />
        );

      case 'assignmentTargets':
        return (
          <AssignmentTargetPicker
            value={fieldValue ?? null}
            onChange={(val) => handleFieldChange(field.name, val)}
            disabledTypes={field.disabledTypes}
            showAutoApply={field.showAutoApply}
            showEmployeeStatus={field.showEmployeeStatus}
          />
        );

      // Sürükle-bırak sıralama; değer = sıralı id dizisi (ör. kademeleri en üstten alta dizmek).
      case 'sortableList':
        return (
          <SortableList
            label={field.label}
            items={field.items || []}
            value={fieldValue ?? []}
            onChange={(ids) => handleFieldChange(field.name, ids)}
            badge={field.badge}
          />
        );

      case 'userGroupCriteria':
        return (
          <GroupCriteriaBuilder
            value={fieldValue ?? []}
            onChange={(val) => handleFieldChange(field.name, val)}
            disabled={field.disabled}
          />
        );

      case 'entitlementRulesEditor':
        return (
          <EntitlementRulesEditor
            label={field.label}
            value={fieldValue ?? null}
            onChange={(val) => handleFieldChange(field.name, val)}
          />
        );

      case 'taskTemplateItems':
        return (
          <TaskTemplateItemsEditor
            label={field.label}
            value={fieldValue ?? []}
            onChange={(val) => handleFieldChange(field.name, val)}
            ownerRoles={field.ownerRoles || []}
            documentTypeOptions={field.documentTypeOptions || []}
            periodOptions={field.periodOptions || []}
            approvalField={field.approvalField || 'requiresHrApproval'}
            error={commonProps.error}
          />
        );

      case 'leavePolicyTemplateVariations':
        return (
          <LeavePolicyTemplateVariationsEditor
            label={field.label}
            value={fieldValue ?? []}
            onChange={(val) => handleFieldChange(field.name, val)}
            error={commonProps.error}
            // Şablonun ülkesi — Türkiye'de yasal yıllık izne devir tavanı kilidi.
            countryCode={formData.countryCode ?? ''}
          />
        );

      case 'phone':
        return (
          <PhoneNumberInput
            label={field.label}
            value={fieldValue ?? ''}
            // Ülke kodu ayrı alanda saklanıyorsa bileşen onu geri ister: E.164
            // gösterimini oradan kurar, numaranın içine yazmaz (çiftlenme olmaz)
            // ve ulusal numaradan ülkeyi tahmin etmek zorunda kalmaz.
            dialCode={field.dialCodeField ? formData[field.dialCodeField] : undefined}
            onChange={(national, dialCode) => {
              if (field.dialCodeField) {
                handleFieldChange(field.name, national);
                handleFieldChange(field.dialCodeField, dialCode);
              } else {
                // Tek alanlı kullanım (ör. ziyaretçi telefonu): ülke kodunu
                // saklayacak ikinci alan yok, o yüzden değer E.164 olarak tek
                // alana yazılır — parçalanırsa ülke kodu tamamen kaybolurdu.
                handleFieldChange(field.name, toE164(dialCode, national));
              }
            }}
            required={field.required}
            error={commonProps.error}
          />
        );

      case 'checkbox':
        return (
          <CheckboxInput
            label={field.label}
            checked={!!fieldValue}
            onChange={(val) => handleFieldChange(field.name, val)}
          />
        );

      case 'pictureUpload':
        return (
          <PictureUpload
            label={field.label}
            value={fieldValue}
            onChange={(val) => handleFieldChange(field.name, val)}
            error={commonProps.error}
            shape={field.shape || 'square'}
            accept={field.accept}
            validTypes={field.validTypes}
            maxSize={field.maxSize}
          />
        );

      default:
        return (
          <TextInput
            label={field.label}
            type="text"
            value={fieldValue ?? ''}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            {...commonProps}
          />
        );
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size={geometry.size}
      duration={150}
      overlayOpacity={0.2}
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100vw',
        // FE-08 — `minWidth: 380` telefonda çekmeceyi ekrandan taşırıyordu:
        // 390px'lik ekranda 12px'lik marj + 380px = 404px, yani sol kenarı
        // (kapat düğmesinin bulunduğu kenar) 14px kesikti. Geometri artık
        // kırılım kovasından geliyor (bkz. layout/shellMetrics.js).
        minWidth: geometry.minWidth,
        margin: geometry.margin,
        borderRadius: geometry.borderRadius,
        height: geometry.height,
        boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
      }}
    >
      <Header>
        <Title>{title}</Title>
        <CloseBtn onClick={onClose}><X size={20} /></CloseBtn>
      </Header>
      <Body>
        {open && (
        <Grid>
          {childrenPosition === 'top' && children && <GridItem $span={12}>{children}</GridItem>}
          {schema.filter((field) => !isFieldHidden(field)).map((field) => {
            // `description` düz metin ya da `(formData) => metin` olabilir — aynı alan
            // form durumuna göre farklı yardım metni taşıyabilsin (ör. kadro talebi modu).
            const description = typeof field.description === 'function'
              ? field.description(formData)
              : field.description;
            return (
              <GridItem key={field.name} $span={field.grid || 12}>
                {renderField(field)}
                {description && <Description>{description}</Description>}
              </GridItem>
            );
          })}
          {childrenPosition !== 'top' && children && <GridItem $span={12}>{children}</GridItem>}
        </Grid>
        )}
      </Body>
      <Footer>
        <Button variant="outline" onClick={onClose} disabled={saving}>
          {t('common.cancel', { defaultValue: 'İptal' })}
        </Button>
        <Button onClick={handleSubmit} disabled={saving || submitDisabled}>
          {saving ? t('common.loading') : (submitLabel || t('common.save', { defaultValue: 'Kaydet' }))}
        </Button>
      </Footer>
    </Drawer>
  );
}
